import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useConnectionState, useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { ConnectionState, RoomEvent, Track } from 'livekit-client';
import ScreenSharePicker, { canUseDesktopPicker } from './ScreenSharePicker';
import { useCallTools } from './CallToolsProvider';

const btnBase =
  'inline-flex flex-col items-center justify-center gap-1 min-w-[4.5rem] px-3 py-2 rounded-xl text-[11px] font-semibold transition-colors disabled:opacity-50';

const MicIcon = ({ off }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M19 11a7 7 0 0 1-14 0M12 18v3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    {off && (
      <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    )}
  </svg>
);

const CameraIcon = ({ off }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="7" width="13" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M16 10.5l5-2.5v8l-5-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    {off && (
      <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    )}
  </svg>
);

const ScreenIcon = ({ on }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    {on && <rect x="6" y="7" width="12" height="7" rx="1" fill="currentColor" opacity="0.85" />}
  </svg>
);

const CaptionIcon = ({ on }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M7 12h4M13 12h4M7 15h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    {on && <circle cx="19" cy="7" r="2.2" fill="currentColor" />}
  </svg>
);

const RecordIcon = ({ on }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4.5" fill="currentColor" />
    {on && <circle cx="12" cy="12" r="4.5" fill="#fff" />}
  </svg>
);

const privacyHint = (kind) => {
  if (kind === 'share') {
    return 'Screen share was blocked. On macOS: System Settings → Privacy & Security → Screen Recording.';
  }
  return 'Camera or microphone was blocked. On macOS: System Settings → Privacy & Security → Camera / Microphone, then allow WorkLoop (or Electron).';
};

const formatMediaError = (err, kind) => {
  const name = err?.name || '';
  const msg = String(err?.message || '');
  if (name === 'NotAllowedError' || name === 'NotFoundError' || name === 'SecurityError') {
    return privacyHint(kind);
  }
  if (/engine not connected/i.test(msg) || /not connected within timeout/i.test(msg)) {
    return 'Still connecting to the call. Try again in a moment.';
  }
  return msg || 'Could not update media';
};

const publicationLive = (participant, source) => {
  const pub = typeof participant?.getTrackPublication === 'function'
    ? participant.getTrackPublication(source)
    : null;
  return Boolean(pub?.track && !pub.isMuted);
};

const waitUntilRoomConnected = (room, timeoutMs = 20000) => {
  if (!room) return Promise.reject(new Error('Call is not ready yet.'));
  if (room.state === ConnectionState.Connected) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let timer = 0;
    const finishOk = () => {
      window.clearTimeout(timer);
      room.off(RoomEvent.Connected, finishOk);
      room.off(RoomEvent.Disconnected, finishFail);
      resolve();
    };
    const finishFail = () => {
      window.clearTimeout(timer);
      room.off(RoomEvent.Connected, finishOk);
      room.off(RoomEvent.Disconnected, finishFail);
      reject(new Error('Disconnected before the call media server was ready.'));
    };
    timer = window.setTimeout(() => {
      room.off(RoomEvent.Connected, finishOk);
      room.off(RoomEvent.Disconnected, finishFail);
      reject(new Error('Could not reach the call media server. Check that LiveKit is running.'));
    }, timeoutMs);
    room.on(RoomEvent.Connected, finishOk);
    room.on(RoomEvent.Disconnected, finishFail);
    if (room.state === ConnectionState.Connected) finishOk();
  });
};

/**
 * Always-visible mic / camera / screenshare bar.
 */
const CallMediaControls = () => {
  const room = useRoomContext();
  const connectionState = useConnectionState(room);
  const {
    localParticipant,
    isMicrophoneEnabled,
    isCameraEnabled,
    isScreenShareEnabled,
    lastMicrophoneError,
    lastCameraError,
  } = useLocalParticipant();
  const callTools = useCallTools();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const didPublishRef = useRef(false);
  const isConnected = connectionState === ConnectionState.Connected;

  useEffect(() => {
    if (!isConnected || !localParticipant || didPublishRef.current) return undefined;
    didPublishRef.current = true;
    let cancelled = false;
    const publish = async () => {
      try {
        if (!cancelled && !publicationLive(localParticipant, Track.Source.Microphone)) {
          await localParticipant.setMicrophoneEnabled(true);
        }
      } catch {
        // lastMicrophoneError
      }
      try {
        if (!cancelled && !publicationLive(localParticipant, Track.Source.Camera)) {
          await localParticipant.setCameraEnabled(true);
        }
      } catch {
        // lastCameraError
      }
    };
    publish();
    return () => { cancelled = true; };
  }, [isConnected, localParticipant]);

  useEffect(() => {
    if (!isConnected) {
      didPublishRef.current = false;
    }
  }, [isConnected]);

  useEffect(() => {
    const parts = [];
    const mic = lastMicrophoneError?.message;
    const cam = lastCameraError?.message;
    if (mic) parts.push(`Mic: ${formatMediaError(lastMicrophoneError, 'mic')}`);
    if (cam) parts.push(`Camera: ${formatMediaError(lastCameraError, 'cam')}`);
    if (parts.length) setError(parts.join(' · '));
  }, [lastMicrophoneError, lastCameraError]);

  const run = useCallback(async (kind, action) => {
    if (!localParticipant) return;
    setBusy(kind);
    setError('');
    try {
      await waitUntilRoomConnected(room);
      await action();
    } catch (err) {
      setError(formatMediaError(err, kind));
    } finally {
      setBusy('');
    }
  }, [localParticipant, room]);

  const toggleMic = () => run('mic', async () => {
    const next = !isMicrophoneEnabled;
    await localParticipant.setMicrophoneEnabled(next);
    if (!next) return;
    await new Promise((resolve) => window.setTimeout(resolve, 250));
    if (!publicationLive(localParticipant, Track.Source.Microphone)) {
      throw new Error(privacyHint('mic'));
    }
  });

  const toggleCam = () => run('cam', async () => {
    const next = !isCameraEnabled;
    await localParticipant.setCameraEnabled(next);
    if (!next) return;
    await new Promise((resolve) => window.setTimeout(resolve, 250));
    if (!publicationLive(localParticipant, Track.Source.Camera)) {
      throw new Error(privacyHint('cam'));
    }
  });

  const stopShare = () => run('share', async () => {
    await localParticipant.setScreenShareEnabled(false);
  });

  const startShareFromPicker = async (source) => {
    setPickerOpen(false);
    await run('share', async () => {
      if (source?.id && typeof window.workloopRealm?.setShareSource === 'function') {
        await window.workloopRealm.setShareSource(source.id);
      }
      await localParticipant.setScreenShareEnabled(true, {
        audio: true,
        selfBrowserSurface: 'include',
      });
    });
  };

  const onShareClick = () => {
    if (isScreenShareEnabled) {
      stopShare();
      return;
    }
    if (canUseDesktopPicker()) {
      setPickerOpen(true);
      return;
    }
    run('share', () => localParticipant.setScreenShareEnabled(true, {
      audio: true,
      selfBrowserSurface: 'include',
    }));
  };

  return (
    <div className="lk-workloop-media-bar flex-shrink-0 z-50 border-t border-white/10 bg-black px-3 py-2.5">
      {!isConnected && (
        <p className="mb-2 text-center text-[11px] text-white/60">
          Connecting to call…
        </p>
      )}
      {isScreenShareEnabled && (
        <div className="mb-2 flex items-center justify-center gap-2">
          <span className="text-[11px] font-semibold text-sky-300">You are sharing your screen</span>
          <button
            type="button"
            onClick={stopShare}
            disabled={busy === 'share'}
            className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-red-600 hover:bg-red-500 text-white"
          >
            Stop sharing
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
        <button
          type="button"
          disabled={!localParticipant || !isConnected || busy === 'mic'}
          aria-pressed={isMicrophoneEnabled}
          title={isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
          onClick={toggleMic}
          className={`${btnBase} ${
            isMicrophoneEnabled ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-red-600 hover:bg-red-500 text-white'
          }`}
        >
          <MicIcon off={!isMicrophoneEnabled} />
          {isMicrophoneEnabled ? 'Mute' : 'Unmute'}
        </button>

        <button
          type="button"
          disabled={!localParticipant || !isConnected || busy === 'cam'}
          aria-pressed={isCameraEnabled}
          title={isCameraEnabled ? 'Turn camera off' : 'Turn camera on'}
          onClick={toggleCam}
          className={`${btnBase} ${
            isCameraEnabled ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-red-600 hover:bg-red-500 text-white'
          }`}
        >
          <CameraIcon off={!isCameraEnabled} />
          {isCameraEnabled ? 'Video' : 'Video off'}
        </button>

        <button
          type="button"
          disabled={!localParticipant || !isConnected || busy === 'share'}
          aria-pressed={isScreenShareEnabled}
          title={isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}
          onClick={onShareClick}
          className={`${btnBase} ${
            isScreenShareEnabled
              ? 'bg-sky-500 hover:bg-sky-400 text-black'
              : 'bg-white/15 hover:bg-white/25 text-white'
          }`}
        >
          <ScreenIcon on={isScreenShareEnabled} />
          {isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}
        </button>

        <button
          type="button"
          disabled={!isConnected}
          aria-pressed={Boolean(callTools?.captionsOn)}
          title={callTools?.captionsOn ? 'Turn captions off' : 'Turn captions on'}
          onClick={() => callTools?.toggleCaptions?.()}
          className={`${btnBase} ${
            callTools?.captionsOn
              ? 'bg-emerald-500 hover:bg-emerald-400 text-black'
              : 'bg-white/15 hover:bg-white/25 text-white'
          }`}
        >
          <CaptionIcon on={Boolean(callTools?.captionsOn)} />
          Captions
        </button>

        <button
          type="button"
          disabled={!isConnected}
          aria-pressed={Boolean(callTools?.recording)}
          title={callTools?.recording ? 'Stop recording and save' : 'Record this call'}
          onClick={() => callTools?.toggleRecording?.()}
          className={`${btnBase} ${
            callTools?.recording
              ? 'bg-red-600 hover:bg-red-500 text-white'
              : 'bg-white/15 hover:bg-white/25 text-white'
          }`}
        >
          <RecordIcon on={Boolean(callTools?.recording)} />
          {callTools?.recording ? 'Stop rec' : 'Record'}
        </button>
      </div>
      {error && (
        <p className="mt-1.5 text-center text-[11px] text-red-300 max-w-3xl mx-auto">{error}</p>
      )}
      <ScreenSharePicker
        open={pickerOpen}
        onPick={startShareFromPicker}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  );
};

export default CallMediaControls;
